/**
 * RBAC — papeis, permissoes e a convencao de nomenclatura.
 *
 * Duas checagens INDEPENDENTES governam toda acao do sistema, e as duas
 * precisam passar:
 *
 *   1. RBAC        — o papel deste usuario tem esta permissao?
 *   2. Entitlement — o condominio contratou o produto de origem desta acao?
 *
 * Elas respondem perguntas diferentes e falham por motivos diferentes: RBAC
 * nega com 403 ("seu perfil nao permite"), entitlement nega com 402 ("seu
 * plano nao inclui") e o front abre o upsell. Misturar as duas faria o
 * sistema oferecer upgrade para quem so nao tem cargo, e esconder venda de
 * quem tem cargo mas nao tem plano.
 *
 * A ligacao entre elas e o NAMESPACE da permissao — ver abaixo.
 */
export const PERMISSION_NAMESPACES = {
    plataforma: {
        label: 'Plataforma Azko',
        scope: 'platform',
        module: null,
    },
    condominio: {
        label: 'Gestao do condominio',
        scope: 'condominium',
        module: 'core',
    },
    encomendas: {
        label: 'Encomendas',
        scope: 'condominium',
        module: 'packages',
    },
    // --- Produtos ainda nao implementados -----------------------------------
    // Declarados desde ja para que o namespace nasca junto com o modulo, e nao
    // depois que as permissoes ja estiverem espalhadas. Enquanto nao houver
    // rota, simplesmente nao existe permissao com estes prefixos.
    portaria: {
        label: 'Portaria e visitantes',
        scope: 'condominium',
        module: 'visitors',
    },
    reservas: {
        label: 'Reservas',
        scope: 'condominium',
        module: 'reservations',
    },
    manutencao: {
        label: 'Manutencao',
        scope: 'condominium',
        module: 'maintenance',
    },
    financeiro: {
        label: 'Financeiro',
        scope: 'condominium',
        module: 'financial',
    },
    comunicacao: {
        label: 'Comunicacao',
        scope: 'condominium',
        module: 'communications',
    },
    assembleias: {
        label: 'Assembleias',
        scope: 'condominium',
        module: 'assemblies',
    },
};
// ---------------------------------------------------------------------------
// Catalogo de permissoes
// ---------------------------------------------------------------------------
/** Backoffice da equipe Azko — escopo global, nenhum condominio envolvido. */
export const PLATFORM_PERMISSIONS = [
    'plataforma:assinantes:visualizar',
    'plataforma:assinantes:editar',
    'plataforma:assinantes:aprovar',
    'plataforma:assinantes:cortar_acesso',
    'plataforma:assinantes:liberar_acesso',
    'plataforma:planos:visualizar',
    'plataforma:planos:criar',
    'plataforma:planos:editar',
    'plataforma:promocoes:visualizar',
    'plataforma:promocoes:criar',
    'plataforma:promocoes:editar',
    'plataforma:promocoes:remover',
    'plataforma:logs:visualizar',
    'plataforma:logs:exportar',
    'plataforma:papeis:gerenciar',
    // Estrutura fisica do condominio (blocos e unidades). Separada de
    // `assinantes:editar` porque sao coisas de peso diferente: corrigir o CNPJ
    // do assinante nao e o mesmo que gerar 200 unidades na conta dele.
    'plataforma:estrutura:visualizar',
    'plataforma:estrutura:editar',
    // Entrar NA CONTA do assinante: a visao geral que o sindico ve, seus
    // moradores, seus porteiros, suas encomendas. Separada de `estrutura` porque
    // aquela e o predio (blocos e unidades) e esta e gente — e o peso e outro:
    // gerar 200 unidades erra um cadastro, mexer em morador e porteiro erra o
    // dado pessoal de quem mora la.
    //
    // Separada tambem de `assinantes:visualizar`, que e a ficha comercial vista
    // de FORA (plano, faturas, status). Estas duas abrem a conta por DENTRO.
    'plataforma:condominios:visualizar',
    'plataforma:condominios:operar',
];
/**
 * Backoffice do sindico + operacao do dia a dia — sempre dentro de UM
 * condominio. `condominio:porteiros:*` e a mesma forma de
 * `plataforma:assinantes:*` um nivel abaixo na hierarquia: cada camada
 * administra a de baixo, dentro do proprio escopo.
 */
export const CONDOMINIUM_PERMISSIONS = [
    'condominio:assinatura:visualizar',
    'condominio:assinatura:solicitar_troca',
    'condominio:faturas:visualizar',
    'condominio:promocoes:visualizar',
    'condominio:logs:visualizar',
    'condominio:porteiros:visualizar',
    'condominio:porteiros:criar',
    'condominio:porteiros:editar',
    'condominio:porteiros:resetar_senha',
    'condominio:porteiros:cortar_acesso',
    'condominio:porteiros:liberar_acesso',
    'condominio:porteiros:visualizar_logs',
    'condominio:porteiros:excluir',
    'condominio:moradores:visualizar',
    'condominio:moradores:criar',
    'condominio:moradores:editar',
    'condominio:moradores:anonimizar',
    'condominio:unidades:visualizar',
    // Blocos entram sob `unidades` de proposito: sao a mesma decisao de produto
    // (a estrutura fisica do predio) e separar geraria um papel que pode criar
    // unidade mas nao o bloco onde ela fica.
    'condominio:unidades:criar',
    'condominio:unidades:editar',
    'condominio:unidades:excluir',
    'encomendas:visualizar',
    'encomendas:registrar',
    'encomendas:entregar',
    // Desfazer o proprio registro. Separada de `registrar` porque quem pode
    // gravar nao necessariamente pode apagar da vista — e a mesma separacao que
    // `moradores:criar` e `moradores:anonimizar` ja fazem.
    'encomendas:cancelar',
];
export const PERMISSIONS = [
    ...PLATFORM_PERMISSIONS,
    ...CONDOMINIUM_PERMISSIONS,
];
const _assertAllNamespaced = true;
void _assertAllNamespaced;
/** Rotulos da tela de papeis (`plataforma:papeis:gerenciar`). */
export const PERMISSION_LABELS = {
    'plataforma:assinantes:visualizar': 'Ver assinantes',
    'plataforma:assinantes:editar': 'Editar cadastro do assinante',
    'plataforma:assinantes:aprovar': 'Aprovar conta em abertura',
    'plataforma:assinantes:cortar_acesso': 'Cortar acesso do assinante',
    'plataforma:assinantes:liberar_acesso': 'Liberar acesso do assinante',
    'plataforma:planos:visualizar': 'Ver planos',
    'plataforma:planos:criar': 'Criar plano',
    'plataforma:planos:editar': 'Editar plano',
    'plataforma:promocoes:visualizar': 'Ver promocoes',
    'plataforma:promocoes:criar': 'Criar promocao',
    'plataforma:promocoes:editar': 'Editar promocao',
    'plataforma:promocoes:remover': 'Remover promocao',
    'plataforma:logs:visualizar': 'Ver logs de auditoria',
    'plataforma:logs:exportar': 'Exportar logs',
    'plataforma:papeis:gerenciar': 'Gerenciar papeis e permissoes',
    'plataforma:estrutura:visualizar': 'Ver blocos e unidades do assinante',
    'plataforma:estrutura:editar': 'Editar blocos e unidades do assinante',
    'plataforma:condominios:visualizar': 'Abrir a conta do assinante por dentro',
    'plataforma:condominios:operar': 'Operar a conta do assinante no lugar dele',
    'condominio:assinatura:visualizar': 'Ver a propria assinatura',
    'condominio:assinatura:solicitar_troca': 'Solicitar troca de plano',
    'condominio:faturas:visualizar': 'Ver faturas',
    'condominio:promocoes:visualizar': 'Ver promocoes disponiveis',
    'condominio:logs:visualizar': 'Ver logs do condominio',
    'condominio:porteiros:visualizar': 'Ver porteiros',
    'condominio:porteiros:criar': 'Criar conta de porteiro',
    'condominio:porteiros:editar': 'Editar porteiro',
    'condominio:porteiros:resetar_senha': 'Redefinir senha do porteiro',
    'condominio:porteiros:cortar_acesso': 'Cortar acesso do porteiro',
    'condominio:porteiros:liberar_acesso': 'Liberar acesso do porteiro',
    'condominio:porteiros:visualizar_logs': 'Ver historico do porteiro',
    'condominio:porteiros:excluir': 'Excluir conta de porteiro',
    'condominio:moradores:visualizar': 'Ver moradores',
    'condominio:moradores:criar': 'Cadastrar morador',
    'condominio:moradores:editar': 'Editar morador',
    'condominio:moradores:anonimizar': 'Apagar dados pessoais de morador (LGPD)',
    'condominio:unidades:visualizar': 'Ver blocos e unidades',
    'condominio:unidades:criar': 'Criar blocos e unidades',
    'condominio:unidades:editar': 'Editar blocos e unidades',
    'condominio:unidades:excluir': 'Arquivar blocos e unidades',
    'encomendas:visualizar': 'Ver encomendas',
    'encomendas:registrar': 'Registrar encomenda',
    'encomendas:entregar': 'Entregar encomenda',
    'encomendas:cancelar': 'Cancelar registro de encomenda',
};
/** Namespace (produto) de uma permissao — primeiro segmento, sempre. */
export function namespaceOf(permission) {
    return permission.slice(0, permission.indexOf(':'));
}
/** Modulo que precisa estar no plano para a permissao valer. */
export function moduleOf(permission) {
    return PERMISSION_NAMESPACES[namespaceOf(permission)].module;
}
export function scopeOf(permission) {
    return PERMISSION_NAMESPACES[namespaceOf(permission)].scope;
}
// ---------------------------------------------------------------------------
// Papeis
// ---------------------------------------------------------------------------
/**
 * Papeis da equipe Azko. Nao sao membership: staff nao pertence a condominio
 * nenhum, e por isso o backoffice da Azko roda fora do `resolveTenant`.
 */
export const PLATFORM_ROLES = [
    'azko_suporte',
    'azko_operacional',
    'azko_admin',
];
export const PLATFORM_ROLE_LABELS = {
    azko_suporte: 'Suporte Azko',
    azko_operacional: 'Operacoes Azko',
    azko_admin: 'Administrador Azko',
};
/**
 * Herança: cada papel acumula tudo do papel abaixo. Manter a cadeia aqui em
 * vez de repetir permissao em cada papel evita o erro classico de conceder
 * algo ao papel do meio e esquecer o de cima.
 */
const PLATFORM_ROLE_PARENT = {
    azko_suporte: null,
    azko_operacional: 'azko_suporte',
    azko_admin: 'azko_operacional',
};
const MEMBERSHIP_ROLE_PARENT = {
    viewer: null,
    concierge: 'viewer',
    manager: 'concierge',
    owner: 'manager',
};
const ROLE_PARENT = {
    ...PLATFORM_ROLE_PARENT,
    ...MEMBERSHIP_ROLE_PARENT,
};
/**
 * Permissoes concedidas DIRETAMENTE a cada papel (sem as herdadas).
 *
 * Sobre sindico: no vocabulario do banco quem administra o condominio e
 * `manager` (rotulo "Sindico") e `owner` e a administradora/dono da conta.
 * Os dois gerenciam porteiros; so `owner` apaga conta em definitivo, porque
 * exclusao nao tem volta e o historico de auditoria fica orfao.
 */
const DIRECT_PERMISSIONS = {
    // --- Condominio -----------------------------------------------------------
    viewer: [
        'condominio:moradores:visualizar',
        'condominio:unidades:visualizar',
        'encomendas:visualizar',
    ],
    // Cancelar fica com o porteiro, e nao so com o sindico, pelo mesmo motivo
    // que `unidades:excluir` desceu para o manager: quem erra e quem esta com a
    // caixa na mao, e o erro tem de ser desfeito nos dois minutos seguintes, nao
    // depois de uma ligacao para a administradora. O que segura o abuso nao e
    // tirar o botao — e o service so aceitar encomenda AGUARDANDO (entrega
    // confirmada nao se desfaz), exigir motivo e gravar autor e hora na timeline
    // e no `audit_log`.
    concierge: ['encomendas:registrar', 'encomendas:entregar', 'encomendas:cancelar'],
    manager: [
        'condominio:assinatura:visualizar',
        'condominio:assinatura:solicitar_troca',
        'condominio:faturas:visualizar',
        'condominio:promocoes:visualizar',
        'condominio:logs:visualizar',
        'condominio:porteiros:visualizar',
        'condominio:porteiros:criar',
        'condominio:porteiros:editar',
        'condominio:porteiros:resetar_senha',
        'condominio:porteiros:cortar_acesso',
        'condominio:porteiros:liberar_acesso',
        'condominio:porteiros:visualizar_logs',
        'condominio:moradores:criar',
        'condominio:moradores:editar',
        'condominio:unidades:criar',
        'condominio:unidades:editar',
        // Arquivar ficava so com o `owner`, pela ideia de que tirar do mapa um
        // lugar com historico de encomenda atras e responsabilidade de quem
        // responde pela conta. Na pratica quem monta o predio e o sindico — e quem
        // duplica uma torre errada tambem. Mandar ele ligar para a administradora
        // para desfazer um erro de dois minutos atras nao se sustenta, e o
        // arquivamento nao apaga nada: a unidade sai da tela e o historico fica.
        'condominio:unidades:excluir',
    ],
    // Apagar dado pessoal nao tem volta e a solicitacao formal do titular chega
    // a quem responde pela conta, nao ao sindico do mes. Mesma logica de
    // `porteiros:excluir`.
    owner: ['condominio:porteiros:excluir', 'condominio:moradores:anonimizar'],
    // --- Plataforma -----------------------------------------------------------
    azko_suporte: [
        'plataforma:assinantes:visualizar',
        'plataforma:planos:visualizar',
        'plataforma:promocoes:visualizar',
        'plataforma:logs:visualizar',
        'plataforma:estrutura:visualizar',
        'plataforma:condominios:visualizar',
    ],
    azko_operacional: [
        'plataforma:assinantes:editar',
        'plataforma:assinantes:aprovar',
        'plataforma:assinantes:cortar_acesso',
        'plataforma:assinantes:liberar_acesso',
        'plataforma:planos:criar',
        'plataforma:planos:editar',
        'plataforma:promocoes:criar',
        'plataforma:promocoes:editar',
        'plataforma:promocoes:remover',
        'plataforma:logs:exportar',
        'plataforma:estrutura:editar',
        'plataforma:condominios:operar',
    ],
    // Poder de conceder poder fica so aqui, e a propria troca de papel e logada.
    azko_admin: ['plataforma:papeis:gerenciar'],
};
/** Permissoes efetivas do papel: as diretas + todas as herdadas. */
export function permissionsForRole(role) {
    const result = new Set();
    let current = role;
    while (current) {
        for (const permission of DIRECT_PERMISSIONS[current])
            result.add(permission);
        current = ROLE_PARENT[current];
    }
    return [...result];
}
export function isPlatformRole(role) {
    return PLATFORM_ROLES.includes(role);
}
/**
 * Matriz completa papel -> permissoes, ja com heranca resolvida. E isto que o
 * seed grava em `role_permissions`; em runtime a fonte da verdade e o banco,
 * porque `plataforma:papeis:gerenciar` permite editar a matriz sem deploy.
 */
export function buildRoleMatrix() {
    const roles = [
        ...PLATFORM_ROLES,
        'owner',
        'manager',
        'concierge',
        'viewer',
    ];
    return Object.fromEntries(roles.map((role) => [role, permissionsForRole(role)]));
}
//# sourceMappingURL=rbac.js.map